import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import type { OriginPerson } from "../../../utils/models";
import type { OriginCopyContext } from "./requestOriginCopy";

/** The language, the department's timezone and day start, job names and name order, for the pure copy in `requestOriginCopy`. */
export const useOriginCopyContext = (): OriginCopyContext => {
	const { t, i18n } = useTranslation();
	const { timezone, chosenDepartment } = useHospitalData();
	const { getNurseById, getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const formatPersonName = useFormatPersonName();
	return useMemo<OriginCopyContext>(() => {
		const jobs = chosenDepartment.jobs;
		return {
			t,
			locale: i18n.language,
			timezone,
			dayStart: chosenDepartment.dayStart,
			jobNameOf: (jobId: string) => jobs?.find((job) => job.id === jobId)?.displayName ?? null,
			personName: (person: OriginPerson) => {
				// A roster nurse reads as the rest of the app shows them; anyone else from the parts the API sent.
				if (person.nurse_id !== null && getNurseById(person.nurse_id)) return getNurseFormattedName(person.nurse_id);
				// An account off the roster has one name field, so there is no order to apply.
				if (person.last_name === null) return person.first_name;
				return formatPersonName({ firstName: person.first_name, lastName: person.last_name });
			},
		};
	}, [t, i18n.language, timezone, chosenDepartment.jobs, chosenDepartment.dayStart, getNurseById, getNurseFormattedName, formatPersonName]);
};
